import {
  StrictMode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { createRoot } from "react-dom/client";
import {
  Avatar,
  Button,
  CellList,
  CellSimple,
  IconButton,
  Input,
  MaxUI,
  Spinner,
  Textarea,
} from "@maxhub/max-ui";
import "@maxhub/max-ui/dist/styles.css";
import {
  ArrowLeft,
  Bell,
  BriefcaseBusiness,
  Check,
  ChevronRight,
  CircleCheck,
  ClipboardCheck,
  Clock3,
  House,
  ImagePlus,
  Images,
  LayoutDashboard,
  LogOut,
  MessageCircle,
  Plus,
  Search,
  ShieldCheck,
  Star,
  Trash2,
  UserCog,
  UserRound,
  UsersRound,
  Wrench,
  X,
} from "lucide-react";
import { legalPages, type LegalPage } from "./legal";
import "./styles.css";
import "./product.css";

declare global {
  interface Window {
    WebApp?: {
      initData?: string;
      initDataUnsafe?: {
        user?: {
          id: number;
          first_name?: string;
          last_name?: string;
          username?: string;
          photo_url?: string;
        };
        start_param?: string;
      };
      BackButton?: {
        show(): void;
        hide(): void;
        onClick(cb: () => void): void;
        offClick(cb: () => void): void;
      };
      enableClosingConfirmation?(): void;
      disableClosingConfirmation?(): void;
      openLink?(url: string): void;
    };
    Telegram?: { WebApp?: { initData?: string } };
  }
}

type Role = "" | "customer" | "provider";
type Status = "unsubmitted" | "pending" | "verified" | "rejected";
type Toast = { id: number; text: string; kind: "success" | "error" };
type Me = {
  user_id: string;
  first_name: string;
  last_name: string;
  username: string;
  photo_url: string;
  role: Role;
  provider_status: Status;
  categories: string[];
  bio: string;
  city: string;
  rating: number;
  jobs_count: number;
};
type Provider = {
  user_id: string;
  name: string;
  username: string;
  photo_url: string;
  categories: string[];
  bio: string;
  city: string;
  rating: number;
  rating_max?: number;
  jobs_count: number;
  contact_url: string | null;
  review_count: number;
  latest_review: string;
  status?: Status;
  blocked?: boolean;
};
type Task = {
  id: number;
  category: string;
  title: string;
  description: string;
  budget: string;
  location: string;
  created_at: string;
  status: string;
  response_count?: number;
  responded?: number;
  provider_name?: string;
  provider_contact_url?: string | null;
  city?: string;
  review_status?: string | null;
  first_name?: string;
  last_name?: string;
};
type ResponseItem = Provider & { response_id: number; status: string };
type ProviderResponse = {
  response_id: number;
  response_status: string;
  id: number;
  title: string;
  category: string;
  budget: string;
  location: string;
  status: string;
  customer_name: string;
  customer_contact_url: string | null;
};
type AdminData = {
  stats: {
    users: number;
    pending: number;
    providers: number;
    tasks: number;
    reviews: number;
    portfolio: number;
    blocked: number;
  };
  providers: Provider[];
  tasks: Task[];
  users: AdminUser[];
  reviews: AdminReview[];
  portfolio: AdminWorkSample[];
  staff: AdminStaff[];
  current_admin: AdminStaff;
  documents: AdminDocument[];
};
type AdminUser = {
  user_id: string;
  first_name: string;
  last_name: string;
  username: string;
  role: Role;
  provider_status: Status;
  blocked: number;
  created_at: string;
};
type AdminReview = {
  id: number;
  task_id: number;
  rating: number;
  text: string;
  status: string;
  created_at: string;
  provider_first_name: string;
  provider_last_name: string;
  customer_first_name: string;
  customer_last_name: string;
  task_title: string;
};
type AdminDocument = LegalPage & { slug: string };
type WorkSample = {
  id: number;
  image_data: string;
  caption: string;
  status?: string;
  created_at?: string;
};
type ProviderReview = {
  id: number;
  rating: number;
  text: string;
  created_at: string;
  first_name: string;
  last_name: string;
  photo_url: string;
};
type ProviderDetail = {
  provider: Provider;
  portfolio: WorkSample[];
  reviews: ProviderReview[];
};
type AdminWorkSample = WorkSample & {
  first_name: string;
  last_name: string;
};
type AdminStaff = {
  id: number;
  username: string;
  display_name: string;
  role: "owner" | "admin" | "moderator";
  active: number;
  created_at?: string;
  last_login?: string | null;
};

const categories = [
  { icon: "🔧", name: "Мастер на час", hint: "Мелкий ремонт и установка" },
  { icon: "🚿", name: "Сантехника", hint: "Трубы, смесители, засоры" },
  { icon: "⚡", name: "Электрика", hint: "Розетки, свет и проводка" },
  { icon: "🪚", name: "Мебель", hint: "Сборка и ремонт мебели" },
  { icon: "🧱", name: "Отделка", hint: "Стены, полы и потолки" },
  { icon: "🧺", name: "Бытовая техника", hint: "Диагностика и ремонт" },
];
const cities = ["Новосибирск"];

function useKeyboardLayout() {
  useEffect(() => {
    const viewport = window.visualViewport;
    const sync = () =>
      document.documentElement.style.setProperty(
        "--rt-viewport-height",
        `${viewport?.height || window.innerHeight}px`,
      );
    const focus = (event: FocusEvent) => {
      const target = event.target;
      if (
        target instanceof HTMLInputElement ||
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLSelectElement
      ) {
        window.setTimeout(
          () => target.scrollIntoView({ block: "center", behavior: "smooth" }),
          220,
        );
      }
    };
    sync();
    viewport?.addEventListener("resize", sync);
    document.addEventListener("focusin", focus);
    return () => {
      viewport?.removeEventListener("resize", sync);
      document.removeEventListener("focusin", focus);
    };
  }, []);
}

async function api<T>(
  path: string,
  options: RequestInit = {},
  adminToken?: string,
): Promise<T> {
  const headers = new Headers(options.headers);
  if (options.body) headers.set("Content-Type", "application/json");
  if (adminToken) headers.set("Authorization", `Bearer ${adminToken}`);
  else headers.set("X-Max-Init-Data", window.WebApp?.initData || "");
  const response = await fetch(`/api${path}`, { ...options, headers });
  if (!response.ok) throw new Error(String(response.status));
  return response.json();
}

function Logo() {
  return (
    <a className="logo" href="/" aria-label="RS Tech">
      <span>RS</span>
      <b>Tech</b>
    </a>
  );
}
function Landing() {
  return (
    <main className="gate">
      <div className="gate-orbit">
        <Wrench />
      </div>
      <Logo />
      <div className="gate-copy">
        <p>Сервис домашних мастеров</p>
        <h1>
          Ремонт начинается
          <br />в MAX
        </h1>
        <span>
          Найдите проверенного специалиста или получите заказ рядом с вами.
        </span>
      </div>
      <div className="gate-card">
        <ShieldCheck />
        <div>
          <b>Откройте RS Tech в MAX</b>
          <p>Сервис заказов и проверенных мастеров работает внутри чат-бота.</p>
        </div>
      </div>
      <footer>
        <a href="/privacy">Конфиденциальность</a>
        <a href="/terms">Условия</a>
        <a href="/details">Реквизиты</a>
        <span>© 2026 RS Tech</span>
      </footer>
    </main>
  );
}
function Toasts({ items }: { items: Toast[] }) {
  return (
    <div className="toasts" aria-live="polite">
      {items.map((item) => (
        <div className={`toast ${item.kind}`} key={item.id}>
          {item.kind === "success" ? <CircleCheck /> : <X />}
          {item.text}
        </div>
      ))}
    </div>
  );
}
function AvatarView({
  name,
  src,
  size = "medium",
}: {
  name: string;
  src?: string;
  size?: "small" | "medium" | "large";
}) {
  return (
    <Avatar.Container size={size}>
      {src ? (
        <Avatar.Image src={src} alt="" />
      ) : (
        <Avatar.Text>{name.slice(0, 1).toUpperCase()}</Avatar.Text>
      )}
    </Avatar.Container>
  );
}
function OpenMax({
  url,
  children = "Написать в MAX",
  variant = "primary",
}: {
  url: string;
  children?: string;
  variant?: "primary" | "secondary";
}) {
  return (
    <Button
      size="small"
      variant={variant}
      onClick={() => {
        if (window.WebApp?.openLink) window.WebApp.openLink(url);
        else window.open(url, "_blank", "noopener");
      }}
      iconBefore={<MessageCircle />}
    >
      {children}
    </Button>
  );
}

function ContactAction({
  taskId,
  directUrl,
  notify,
}: {
  taskId: number;
  directUrl?: string | null;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [loading, setLoading] = useState(false);
  if (directUrl) return <OpenMax url={directUrl} />;
  const requestContact = async () => {
    setLoading(true);
    try {
      await api(`/tasks/${taskId}/contact`, { method: "POST", body: "{}" });
      notify("Контакт отправлен вам в чат с ботом");
    } catch {
      notify("Не удалось отправить контакт", "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      size="small"
      loading={loading}
      onClick={requestContact}
      iconBefore={<MessageCircle />}
    >
      Связаться
    </Button>
  );
}

function ProviderContactAction({
  provider,
  notify,
  label = "Связаться",
}: {
  provider: Provider;
  notify: (text: string, kind?: Toast["kind"]) => void;
  label?: string;
}) {
  const [loading, setLoading] = useState(false);
  if (provider.contact_url) return <OpenMax url={provider.contact_url}>{label}</OpenMax>;
  const requestContact = async () => {
    setLoading(true);
    try {
      await api(`/providers/${encodeURIComponent(provider.user_id)}/contact`, {
        method: "POST",
        body: "{}",
      });
      notify("Контакт исполнителя отправлен вам в чат с ботом");
    } catch {
      notify("Не удалось отправить контакт исполнителя", "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <Button
      size="small"
      loading={loading}
      onClick={requestContact}
      iconBefore={<MessageCircle />}
    >
      {label}
    </Button>
  );
}

function AppTopbar({ me }: { me: Me }) {
  return (
    <header className="rt-topbar">
      <div className="rt-shell rt-topbar-inner">
        <Logo />
        <div className="rt-top-actions">
          <span className="rt-role">
            {me.role === "provider" ? "Исполнитель" : "Заказчик"}
          </span>
          <IconButton size="small" variant="ghost" aria-label="Уведомления">
            <Bell />
          </IconButton>
          <AvatarView
            name={me.first_name || "R"}
            src={me.photo_url}
            size="small"
          />
        </div>
      </div>
    </header>
  );
}

function BottomNav({
  value,
  onChange,
  userRole,
}: {
  value: string;
  onChange: (value: string) => void;
  userRole: Role;
}) {
  const items =
    userRole === "customer"
      ? [
          { id: "home", label: "Главная", icon: House },
          { id: "tasks", label: "Мои заявки", icon: ClipboardCheck },
          { id: "profile", label: "Профиль", icon: UserRound },
        ]
      : [
          { id: "orders", label: "Заказы", icon: BriefcaseBusiness },
          { id: "responses", label: "Отклики", icon: MessageCircle },
          { id: "profile", label: "Профиль", icon: UserRound },
        ];
  return (
    <nav className="rt-bottom-nav" aria-label="Разделы">
      {items.map((item) => (
        <button
          key={item.id}
          className={value === item.id ? "active" : ""}
          onClick={() => onChange(item.id)}
        >
          <item.icon />
          <span>{item.label}</span>
        </button>
      ))}
    </nav>
  );
}

function RolePicker({
  me,
  refresh,
  notify,
}: {
  me: Me;
  refresh: () => Promise<void>;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const choose = async (role: Role) => {
    try {
      await api("/me/role", { method: "POST", body: JSON.stringify({ role }) });
      await refresh();
    } catch {
      notify("Не удалось сохранить роль", "error");
    }
  };
  return (
    <div className="rt-onboarding">
      <Logo />
      <div className="rt-onboarding-copy">
        <span>Добро пожаловать, {me.first_name}</span>
        <h1>
          Как вы хотите
          <br />
          использовать RS Tech?
        </h1>
        <p>Роль можно поменять позже в профиле.</p>
      </div>
      <div className="rt-role-grid">
        <button onClick={() => choose("customer")}>
          <span className="rt-role-icon blue">
            <House />
          </span>
          <b>Я заказчик</b>
          <p>Ищу проверенного мастера и размещаю свои заявки.</p>
          <ChevronRight />
        </button>
        <button onClick={() => choose("provider")}>
          <span className="rt-role-icon dark">
            <Wrench />
          </span>
          <b>Я исполнитель</b>
          <p>Прохожу проверку, смотрю заказы и отправляю отклики.</p>
          <ChevronRight />
        </button>
      </div>
      <small>
        Продолжая, вы соглашаетесь с <a href="/terms">условиями сервиса</a>.
      </small>
    </div>
  );
}

function RatingStars({ value }: { value: number }) {
  return (
    <span className="rt-stars" aria-label={`Рейтинг ${value} из 10`}>
      {Array.from({ length: 10 }, (_, index) => (
        <Star className={index < Math.round(value) ? "filled" : ""} key={index} />
      ))}
    </span>
  );
}

function ProviderCard({
  provider,
  onOpen,
  notify,
}: {
  provider: Provider;
  onOpen: () => void;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  return (
    <article className="rt-provider-card">
      <div className="rt-provider-head">
        <AvatarView name={provider.name} src={provider.photo_url} />
        <div>
          <b>{provider.name}</b>
          <span>
            <Star /> {provider.rating}/10 · {provider.jobs_count} заказов
          </span>
        </div>
        <ShieldCheck className="rt-verified" />
      </div>
      <p>{provider.bio || "Проверенный специалист RS Tech"}</p>
      {provider.latest_review && (
        <blockquote>«{provider.latest_review}»</blockquote>
      )}
      <div className="rt-tags">
        {provider.categories.slice(0, 2).map((x) => (
          <span key={x}>{x}</span>
        ))}
      </div>
      <div className="rt-provider-bottom">
        <small>
          {provider.city || "Город не указан"} · {provider.review_count || 0}{" "}
          отзывов
        </small>
        <div className="rt-provider-actions">
          <Button size="small" variant="secondary" onClick={onOpen}>
            Профиль
          </Button>
          <ProviderContactAction provider={provider} notify={notify} />
        </div>
      </div>
    </article>
  );
}

function ProviderProfileSheet({
  providerId,
  close,
  notify,
}: {
  providerId: string;
  close: () => void;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [detail, setDetail] = useState<ProviderDetail | null>(null);
  const [tab, setTab] = useState<"about" | "portfolio" | "reviews">("about");
  useEffect(() => {
    let active = true;
    void api<ProviderDetail>(`/providers/${encodeURIComponent(providerId)}`)
      .then((value) => active && setDetail(value))
      .catch(() => notify("Не удалось открыть профиль", "error"));
    return () => {
      active = false;
    };
  }, [notify, providerId]);
  return (
    <div className="rt-overlay" role="presentation" onMouseDown={(event) => event.target === event.currentTarget && close()}>
      <section className="rt-sheet rt-provider-profile-sheet" aria-label="Профиль исполнителя">
        <div className="rt-sheet-handle" />
        <header>
          <IconButton aria-label="Закрыть" onClick={close}>
            <X />
          </IconButton>
          <b>Исполнитель</b>
          <span />
        </header>
        {!detail ? (
          <div className="rt-profile-loading"><Spinner /></div>
        ) : (
          <>
            <div className="rt-provider-hero">
              <AvatarView name={detail.provider.name} src={detail.provider.photo_url} size="large" />
              <div>
                <span>{detail.provider.categories[0] || "Мастер"}</span>
                <h2>{detail.provider.name}</h2>
                <RatingStars value={detail.provider.rating} />
                <small>{detail.provider.rating}/10, {detail.provider.review_count} отзывов, {detail.provider.jobs_count} заказов</small>
              </div>
              <ShieldCheck />
            </div>
            <nav className="rt-profile-tabs" aria-label="Разделы профиля">
              <button className={tab === "about" ? "active" : ""} onClick={() => setTab("about")}>О мастере</button>
              <button className={tab === "portfolio" ? "active" : ""} onClick={() => setTab("portfolio")}>Работы <span>{detail.portfolio.length}</span></button>
              <button className={tab === "reviews" ? "active" : ""} onClick={() => setTab("reviews")}>Отзывы <span>{detail.reviews.length}</span></button>
            </nav>
            {tab === "about" && (
              <div className="rt-profile-pane">
                <h3>Специализация</h3>
                <div className="rt-tags">{detail.provider.categories.map((item) => <span key={item}>{item}</span>)}</div>
                <h3>О себе</h3>
                <p>{detail.provider.bio || "Исполнитель пока не добавил описание."}</p>
                <div className="rt-profile-facts"><span>Город</span><b>{detail.provider.city}</b><span>Проверка</span><b>Пройдена вручную</b></div>
              </div>
            )}
            {tab === "portfolio" && (
              detail.portfolio.length ? <div className="rt-portfolio-grid">{detail.portfolio.map((item) => <figure key={item.id}><img src={item.image_data} alt={item.caption} /><figcaption>{item.caption}</figcaption></figure>)}</div> : <div className="rt-profile-empty"><Images /><b>Работы ещё не добавлены</b><span>Исполнитель сможет пополнить портфолио позже.</span></div>
            )}
            {tab === "reviews" && (
              detail.reviews.length ? <div className="rt-profile-reviews">{detail.reviews.map((review) => <article key={review.id}><div><AvatarView name={`${review.first_name} ${review.last_name}`} src={review.photo_url} size="small" /><b>{review.first_name} {review.last_name}</b><strong>{review.rating}/10</strong></div><RatingStars value={review.rating} /><p>{review.text}</p></article>)}</div> : <div className="rt-profile-empty"><Star /><b>Отзывов пока нет</b><span>Отзывы появляются после завершённых заказов и модерации.</span></div>
            )}
            <div className="rt-profile-action"><ProviderContactAction provider={detail.provider} notify={notify} label="Написать исполнителю" /></div>
          </>
        )}
      </section>
    </div>
  );
}

function CreateTask({
  close,
  created,
  notify,
  category,
}: {
  close: () => void;
  created: () => void;
  notify: (text: string, kind?: Toast["kind"]) => void;
  category?: string;
}) {
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    window.WebApp?.enableClosingConfirmation?.();
    const back = () => close();
    window.WebApp?.BackButton?.show();
    window.WebApp?.BackButton?.onClick(back);
    return () => {
      window.WebApp?.disableClosingConfirmation?.();
      window.WebApp?.BackButton?.offClick(back);
      window.WebApp?.BackButton?.hide();
    };
  }, [close]);
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    try {
      await api("/tasks", {
        method: "POST",
        body: JSON.stringify(
          Object.fromEntries(new FormData(event.currentTarget)),
        ),
      });
      notify("Заявка опубликована");
      created();
      close();
    } catch {
      notify("Проверьте заполнение заявки", "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <div
      className="rt-overlay"
      role="presentation"
      onMouseDown={(e) => e.target === e.currentTarget && close()}
    >
      <dialog open className="rt-sheet" aria-labelledby="new-task-title">
        <div className="rt-sheet-handle" />
        <header>
          <IconButton
            size="small"
            variant="ghost"
            onClick={close}
            aria-label="Закрыть"
          >
            <ArrowLeft />
          </IconButton>
          <h2 id="new-task-title">Новая заявка</h2>
          <span />
        </header>
        <form onSubmit={submit}>
          <label htmlFor="category">Категория</label>
          <select
            id="category"
            name="category"
            defaultValue={category || categories[0].name}
          >
            {categories.map((x) => (
              <option key={x.name}>{x.name}</option>
            ))}
          </select>
          <label htmlFor="title">Что нужно сделать</label>
          <Input
            id="title"
            name="title"
            minLength={4}
            maxLength={120}
            required
            placeholder="Например, заменить смеситель"
            withClearButton
          />
          <label htmlFor="description">Подробности</label>
          <Textarea
            id="description"
            name="description"
            minLength={10}
            maxLength={2000}
            required
            placeholder="Опишите задачу, сроки и важные детали"
          />
          <label htmlFor="task-city">Город</label>
          <select id="task-city" name="city" defaultValue="Новосибирск">
            {cities.map((city) => (
              <option key={city}>{city}</option>
            ))}
          </select>
          <div className="rt-two">
            <div>
              <label htmlFor="budget">Бюджет</label>
              <Input
                id="budget"
                name="budget"
                maxLength={40}
                placeholder="По договорённости"
              />
            </div>
            <div>
              <label htmlFor="location">Район или адрес</label>
              <Input
                id="location"
                name="location"
                maxLength={120}
                placeholder="Например, Заельцовский"
              />
            </div>
          </div>
          <Button size="large" stretched loading={loading}>
            Опубликовать
          </Button>
          <small>Заявку увидят только верифицированные исполнители.</small>
        </form>
      </dialog>
    </div>
  );
}

function CustomerTaskCard({
  task,
  refresh,
  notify,
}: {
  task: Task;
  refresh: () => void;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [open, setOpen] = useState(false);
  const [responses, setResponses] = useState<ResponseItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [rating, setRating] = useState(10);
  const [reviewText, setReviewText] = useState("");
  const showResponses = async () => {
    setOpen((x) => !x);
    if (!open && !responses.length) {
      setLoading(true);
      try {
        const data = await api<{ responses: ResponseItem[] }>(
          `/tasks/${task.id}/responses`,
        );
        setResponses(data.responses);
      } catch {
        notify("Не удалось загрузить отклики", "error");
      } finally {
        setLoading(false);
      }
    }
  };
  const accept = async (responseId: number) => {
    try {
      await api(`/tasks/${task.id}/accept/${responseId}`, {
        method: "POST",
        body: "{}",
      });
      notify("Исполнитель выбран");
      await showResponses();
      refresh();
    } catch {
      notify("Не удалось принять отклик", "error");
    }
  };
  const complete = async () => {
    try {
      await api(`/tasks/${task.id}/complete`, { method: "POST", body: "{}" });
      notify("Заказ отмечен выполненным");
      refresh();
    } catch {
      notify("Не удалось завершить заказ", "error");
    }
  };
  const review = async () => {
    try {
      await api(`/tasks/${task.id}/review`, {
        method: "POST",
        body: JSON.stringify({ rating, text: reviewText }),
      });
      notify("Отзыв отправлен на модерацию");
      refresh();
    } catch {
      notify("Напишите отзыв не короче 10 символов", "error");
    }
  };
  return (
    <article className="rt-task">
      <div className="rt-task-top">
        <span>{task.category}</span>
        <em className={`rt-status ${task.status}`}>
          {task.status === "completed"
            ? "Выполнен"
            : task.status === "matched"
              ? "Исполнитель выбран"
              : "Ищем мастера"}
        </em>
      </div>
      <h3>{task.title}</h3>
      <p>{task.description}</p>
      <div className="rt-task-info">
        <b>{task.budget || "По договорённости"}</b>
        <span>{task.location || "Адрес не указан"}</span>
      </div>
      {task.status === "matched" || task.status === "completed" ? (
        <div className="rt-match">
          <CircleCheck />
          <div>
            <b>{task.provider_name || "Исполнитель выбран"}</b>
            <span>Контакт открыт после подтверждения</span>
          </div>
          <ContactAction
            taskId={task.id}
            directUrl={task.provider_contact_url}
            notify={notify}
          />
        </div>
      ) : (
        <Button size="small" variant="secondary" onClick={showResponses}>
          {open ? "Скрыть отклики" : `Отклики · ${task.response_count || 0}`}
        </Button>
      )}
      {task.status === "matched" && (
        <Button
          className="rt-complete"
          size="small"
          variant="secondary"
          stretched
          onClick={complete}
        >
          Завершить заказ
        </Button>
      )}
      {task.status === "completed" && !task.review_status && (
        <div className="rt-review-form">
          <b>Оцените исполнителя</b>
          <div className="rt-rating" aria-label="Оценка">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((value) => (
              <button
                key={value}
                type="button"
                className={value <= rating ? "active" : ""}
                onClick={() => setRating(value)}
                aria-label={`${value} из 10`}
              >
                <Star />
              </button>
            ))}
          </div>
          <Textarea
            value={reviewText}
            onChange={(event) => setReviewText(event.target.value)}
            placeholder="Что понравилось в работе?"
            maxLength={1000}
          />
          <Button size="small" onClick={review}>
            Отправить отзыв
          </Button>
        </div>
      )}
      {task.review_status && (
        <div className="rt-review-state">
          <Clock3 /> Отзыв{" "}
          {task.review_status === "published"
            ? "опубликован"
            : task.review_status === "rejected"
              ? "отклонён"
              : "на модерации"}
        </div>
      )}
      {open && (
        <div className="rt-responses">
          {loading ? (
            <Spinner />
          ) : responses.length ? (
            responses.map((item) => (
              <div className="rt-response" key={item.response_id}>
                <AvatarView
                  name={item.name}
                  src={item.photo_url}
                  size="small"
                />
                <div>
                  <b>{item.name}</b>
                  <span>
                    <Star /> {item.rating}/10 · {item.city}
                  </span>
                </div>
                {item.status === "accepted" && item.contact_url ? (
                  <OpenMax url={item.contact_url} />
                ) : (
                  <Button size="small" onClick={() => accept(item.response_id)}>
                    Выбрать
                  </Button>
                )}
              </div>
            ))
          ) : (
            <p>Откликов пока нет</p>
          )}
        </div>
      )}
    </article>
  );
}

function RoleSwitch({ me, refresh }: { me: Me; refresh: () => Promise<void> }) {
  const change = async () => {
    await api("/me/role", {
      method: "POST",
      body: JSON.stringify({
        role: me.role === "customer" ? "provider" : "customer",
      }),
    });
    await refresh();
  };
  return (
    <CellList mode="island">
      <CellSimple
        title={
          me.role === "customer"
            ? "Перейти в режим исполнителя"
            : "Перейти в режим заказчика"
        }
        subtitle={
          me.role === "customer"
            ? "Задания будут доступны после проверки"
            : "Искать мастеров и создавать заявки"
        }
        before={me.role === "customer" ? <Wrench /> : <House />}
        showChevron
        onClick={change}
      />
      <CellSimple
        title="Документы сервиса"
        subtitle="Условия и конфиденциальность"
        before={<ShieldCheck />}
        showChevron
        link="/terms"
      />
      <CellSimple
        title="Реквизиты сервиса"
        subtitle="Самозанятый и контактные данные"
        before={<ShieldCheck />}
        showChevron
        link="/details"
      />
    </CellList>
  );
}

function CustomerApp({
  me,
  refreshMe,
  notify,
}: {
  me: Me;
  refreshMe: () => Promise<void>;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [view, setView] = useState("home");
  const [providers, setProviders] = useState<Provider[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [modal, setModal] = useState<string | null>(null);
  const [selectedProvider, setSelectedProvider] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, t] = await Promise.all([
        api<{ providers: Provider[] }>(
          `/providers${filter ? `?category=${encodeURIComponent(filter)}` : ""}`,
        ),
        api<{ tasks: Task[] }>("/tasks"),
      ]);
      setProviders(p.providers);
      setTasks(t.tasks);
    } catch {
      notify("Не удалось обновить данные", "error");
    } finally {
      setLoading(false);
    }
  }, [filter, notify]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  return (
    <>
      <AppTopbar me={me} />
      <main className="rt-shell rt-content">
        {view === "home" && (
          <>
            <section className="rt-welcome">
              <div>
                <span>Здравствуйте, {me.first_name}</span>
                <h1>
                  Надёжный мастер
                  <br />
                  для любой задачи
                </h1>
              </div>
              <Button
                size="medium"
                iconBefore={<Plus />}
                onClick={() => setModal("")}
              >
                Создать заявку
              </Button>
            </section>
            <button
              className="rt-search"
              onClick={() =>
                document
                  .getElementById("masters")
                  ?.scrollIntoView({ behavior: "smooth" })
              }
            >
              <Search />
              <span>Найти исполнителя</span>
            </button>
            <section className="rt-section">
              <div className="rt-section-head">
                <div>
                  <h2>Что нужно сделать?</h2>
                  <p>Выберите направление</p>
                </div>
              </div>
              <div className="rt-categories">
                {categories.map((item) => (
                  <button
                    key={item.name}
                    onClick={() => {
                      setFilter(item.name);
                      window.setTimeout(
                        () =>
                          document
                            .getElementById("masters")
                            ?.scrollIntoView({ behavior: "smooth" }),
                        0,
                      );
                    }}
                  >
                    <span>{item.icon}</span>
                    <b>{item.name}</b>
                    <small>{item.hint}</small>
                  </button>
                ))}
              </div>
            </section>
            <section className="rt-section" id="masters">
              <div className="rt-section-head">
                <div>
                  <h2>
                    {filter
                      ? `Исполнители · ${filter}`
                      : "Популярные исполнители"}
                  </h2>
                  <p>Все профили проверены вручную</p>
                </div>
                {filter && (
                  <Button
                    size="xsmall"
                    variant="ghost"
                    onClick={() => setFilter("")}
                  >
                    Сбросить
                  </Button>
                )}
              </div>
              {loading ? (
                <div className="rt-loader">
                  <Spinner />
                </div>
              ) : providers.length ? (
                <div className="rt-provider-grid">
                  {providers.map((x) => (
                    <ProviderCard provider={x} key={x.user_id} onOpen={() => setSelectedProvider(x.user_id)} notify={notify} />
                  ))}
                </div>
              ) : (
                <div className="rt-empty">
                  <UsersRound />
                  <b>Исполнители скоро появятся</b>
                  <p>Мы проверяем специалистов перед публикацией.</p>
                </div>
              )}
            </section>
          </>
        )}
        {view === "tasks" && (
          <>
            <div className="rt-page-head">
              <div>
                <h1>Мои заявки</h1>
                <p>Только ваши размещённые заказы</p>
              </div>
              <Button
                size="small"
                iconBefore={<Plus />}
                onClick={() => setModal("")}
              >
                Создать
              </Button>
            </div>
            {loading ? (
              <div className="rt-loader">
                <Spinner />
              </div>
            ) : tasks.length ? (
              <div className="rt-task-list">
                {tasks.map((task) => (
                  <CustomerTaskCard
                    key={task.id}
                    task={task}
                    refresh={() => void load()}
                    notify={notify}
                  />
                ))}
              </div>
            ) : (
              <div className="rt-empty tall">
                <ClipboardCheck />
                <b>Заявок пока нет</b>
                <p>
                  Опишите задачу — проверенные исполнители смогут откликнуться.
                </p>
                <Button onClick={() => setModal("")}>Создать заявку</Button>
              </div>
            )}
          </>
        )}
        {view === "profile" && <Profile me={me} refreshMe={refreshMe} notify={notify} />}
      </main>
      <BottomNav userRole="customer" value={view} onChange={setView} />
      {modal !== null && (
        <CreateTask
          category={modal || undefined}
          close={() => setModal(null)}
          created={() => void load()}
          notify={notify}
        />
      )}
      {selectedProvider && (
        <ProviderProfileSheet providerId={selectedProvider} close={() => setSelectedProvider(null)} notify={notify} />
      )}
    </>
  );
}

function Verification({
  me,
  refreshMe,
  notify,
}: {
  me: Me;
  refreshMe: () => Promise<void>;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [selected, setSelected] = useState<string[]>(me.categories);
  const [loading, setLoading] = useState(false);
  if (me.provider_status === "pending")
    return (
      <div className="rt-verification-state">
        <span>
          <Clock3 />
        </span>
        <h1>Профиль на проверке</h1>
        <p>
          Администратор проверит данные. После подтверждения вам откроется лента
          заказов.
        </p>
        <Button variant="secondary" onClick={() => void refreshMe()}>
          Обновить статус
        </Button>
      </div>
    );
  const submit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const form = new FormData(event.currentTarget);
    try {
      await api("/me/provider", {
        method: "POST",
        body: JSON.stringify({
          city: form.get("city"),
          bio: form.get("bio"),
          categories: selected,
        }),
      });
      notify("Анкета отправлена на проверку");
      await refreshMe();
    } catch {
      notify("Заполните город, описание и категорию", "error");
    } finally {
      setLoading(false);
    }
  };
  return (
    <section className="rt-verification">
      <span className="rt-role-icon dark">
        <ShieldCheck />
      </span>
      <h1>
        {me.provider_status === "rejected"
          ? "Обновите анкету"
          : "Верификация исполнителя"}
      </h1>
      <p>
        Заполните профиль. Задания откроются после ручной проверки
        администратором.
      </p>
      {me.provider_status === "rejected" && (
        <div className="rt-alert">
          Профиль требует доработки. Уточните опыт и направления работ.
        </div>
      )}
      <form onSubmit={submit}>
        <label htmlFor="city">Город</label>
        <select id="city" name="city" defaultValue={me.city || "Новосибирск"}>
          {cities.map((city) => (
            <option key={city}>{city}</option>
          ))}
        </select>
        <span className="rt-field-label">Категории работ</span>
        <div className="rt-check-grid">
          {categories.map((item) => (
            <button
              type="button"
              className={selected.includes(item.name) ? "selected" : ""}
              key={item.name}
              onClick={() =>
                setSelected((x) =>
                  x.includes(item.name)
                    ? x.filter((y) => y !== item.name)
                    : [...x, item.name],
                )
              }
            >
              <span>{item.icon}</span>
              {item.name}
              {selected.includes(item.name) && <Check />}
            </button>
          ))}
        </div>
        <label htmlFor="bio">О себе и опыте</label>
        <Textarea
          id="bio"
          name="bio"
          defaultValue={me.bio}
          minLength={20}
          required
          placeholder="Расскажите об опыте, инструментах и гарантиях на работу"
        />
        <Button size="large" stretched loading={loading}>
          Отправить на проверку
        </Button>
      </form>
    </section>
  );
}

function ProviderTask({
  task,
  refresh,
  notify,
}: {
  task: Task;
  refresh: () => void;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const respond = async () => {
    try {
      const result = await api<{ already_responded: boolean }>(
        `/tasks/${task.id}/respond`,
        { method: "POST", body: "{}" },
      );
      notify(
        result.already_responded
          ? "Вы уже откликались"
          : "Отклик отправлен заказчику",
      );
      refresh();
    } catch {
      notify("Не удалось отправить отклик", "error");
    }
  };
  return (
    <article className="rt-task provider">
      <div className="rt-task-top">
        <span>{task.category}</span>
        <time>
          <Clock3 /> {task.created_at}
        </time>
      </div>
      <h3>{task.title}</h3>
      <p>{task.description}</p>
      <div className="rt-task-footer">
        <div>
          <b>{task.budget || "По договорённости"}</b>
          <span>{task.location || "Район не указан"}</span>
        </div>
        <Button
          size="small"
          variant={task.responded ? "secondary" : "primary"}
          disabled={Boolean(task.responded)}
          onClick={respond}
        >
          {task.responded ? "Отклик отправлен" : "Откликнуться"}
        </Button>
      </div>
    </article>
  );
}

function ProviderApp({
  me,
  refreshMe,
  notify,
}: {
  me: Me;
  refreshMe: () => Promise<void>;
  notify: (text: string, kind?: Toast["kind"]) => void;
}) {
  const [view, setView] = useState("orders");
  const [tasks, setTasks] = useState<Task[]>([]);
  const [responses, setResponses] = useState<ProviderResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([
        api<{ tasks: Task[] }>("/tasks"),
        api<{ responses: ProviderResponse[] }>("/responses"),
      ]);
      setTasks(t.tasks);
      setResponses(r.responses);
    } catch {
      notify("Не удалось обновить данные", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => {
    const timer = window.setTimeout(() => {
      if (me.provider_status === "verified") void load();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [load, me.provider_status]);
  if (me.provider_status !== "verified")
    return (
      <>
        <AppTopbar me={me} />
        <main className="rt-shell rt-content">
          <Verification me={me} refreshMe={refreshMe} notify={notify} />
        </main>
        <BottomNav
          userRole="provider"
          value="profile"
          onChange={(value) =>
            value === "profile"
              ? undefined
              : notify("Задания откроются после верификации", "error")
          }
        />
      </>
    );
  return (
    <>
      <AppTopbar me={me} />
      <main className="rt-shell rt-content">
        {view === "orders" && (
          <>
            <div className="rt-page-head">
              <div>
                <h1>Новые заказы</h1>
                <p>Заявки заказчиков рядом с вами</p>
              </div>
              <span className="rt-count">{tasks.length}</span>
            </div>
            {loading ? (
              <div className="rt-loader">
                <Spinner />
              </div>
            ) : tasks.length ? (
              <div className="rt-task-list">
                {tasks.map((task) => (
                  <ProviderTask
                    key={task.id}
                    task={task}
                    refresh={() => void load()}
                    notify={notify}
                  />
                ))}
              </div>
            ) : (
              <div className="rt-empty tall">
                <BriefcaseBusiness />
                <b>Новых заказов нет</b>
                <p>
                  Мы пришлём уведомление в MAX, когда появятся подходящие
                  заявки.
                </p>
              </div>
            )}
          </>
        )}
        {view === "responses" && (
          <>
            <div className="rt-page-head">
              <div>
                <h1>Мои отклики</h1>
                <p>Связаться можно после ответа заказчика</p>
              </div>
            </div>
            {responses.length ? (
              <div className="rt-task-list">
                {responses.map((item) => (
                  <article className="rt-response-card" key={item.response_id}>
                    <div>
                      <span>{item.category}</span>
                      <em className={`rt-status ${item.response_status}`}>
                        {item.response_status === "accepted"
                          ? "Принят"
                          : item.response_status === "rejected"
                            ? "Другой исполнитель"
                            : "На рассмотрении"}
                      </em>
                    </div>
                    <h3>{item.title}</h3>
                    <p>
                      {item.budget || "По договорённости"} ·{" "}
                      {item.location || "Район не указан"}
                    </p>
                    {item.response_status === "accepted" ? (
                      <div className="rt-match">
                        <CircleCheck />
                        <div>
                          <b>{item.customer_name || "Заказчик"}</b>
                          <span>Заказчик принял ваш отклик</span>
                        </div>
                        <ContactAction
                          taskId={item.id}
                          directUrl={item.customer_contact_url}
                          notify={notify}
                        />
                      </div>
                    ) : (
                      <small>
                        Контакт заказчика скрыт до принятия отклика.
                      </small>
                    )}
                  </article>
                ))}
              </div>
            ) : (
              <div className="rt-empty tall">
                <MessageCircle />
                <b>Откликов пока нет</b>
                <p>Выберите подходящий заказ и отправьте отклик.</p>
              </div>
            )}
          </>
        )}
        {view === "profile" && <Profile me={me} refreshMe={refreshMe} notify={notify} />}
      </main>
      <BottomNav userRole="provider" value={view} onChange={setView} />
    </>
  );
}

async function preparePortfolioImage(file: File): Promise<string> {
  if (!file.type.startsWith("image/")) throw new Error("invalid_file");
  const bitmap = await createImageBitmap(file);
  const maxSide = 1280;
  const scale = Math.min(1, maxSide / Math.max(bitmap.width, bitmap.height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(bitmap.width * scale));
  canvas.height = Math.max(1, Math.round(bitmap.height * scale));
  canvas.getContext("2d")?.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  bitmap.close();
  const blob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob((value) => value ? resolve(value) : reject(new Error("image_encode_failed")), "image/jpeg", 0.82),
  );
  if (blob.size > 1_000_000) throw new Error("image_too_large");
  return await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

function ProviderPortfolioManager({ notify }: { notify: (text: string, kind?: Toast["kind"]) => void }) {
  const [items, setItems] = useState<WorkSample[]>([]);
  const [imageData, setImageData] = useState("");
  const [processing, setProcessing] = useState(false);
  const load = useCallback(async () => {
    try {
      const result = await api<{ portfolio: WorkSample[] }>("/me/portfolio");
      setItems(result.portfolio);
    } catch {
      notify("Не удалось загрузить портфолио", "error");
    }
  }, [notify]);
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const add = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!imageData) return notify("Выберите фотографию", "error");
    setProcessing(true);
    try {
      const form = new FormData(event.currentTarget);
      await api("/me/portfolio", { method: "POST", body: JSON.stringify({ image_data: imageData, caption: form.get("caption") }) });
      setImageData("");
      event.currentTarget.reset();
      notify("Работа отправлена на модерацию");
      await load();
    } catch {
      notify("Не удалось добавить работу. Проверьте размер и описание", "error");
    } finally {
      setProcessing(false);
    }
  };
  const remove = async (id: number) => {
    try {
      await api(`/me/portfolio/${id}/delete`, { method: "POST", body: "{}" });
      await load();
    } catch {
      notify("Не удалось удалить работу", "error");
    }
  };
  return (
    <section className="rt-own-portfolio">
      <div className="rt-section-head"><div><h2>Мои работы</h2><p>До 12 фотографий, публикация после проверки</p></div><span>{items.length}/12</span></div>
      <form onSubmit={add}>
        <label className="rt-upload-field">
          {imageData ? <img src={imageData} alt="Предпросмотр" /> : <><ImagePlus /><b>Добавить фотографию</b><span>JPG, PNG или WebP</span></>}
          <input type="file" accept="image/jpeg,image/png,image/webp" onChange={async (event) => {
            const file = event.target.files?.[0];
            if (!file) return;
            setProcessing(true);
            try { setImageData(await preparePortfolioImage(file)); }
            catch { notify("Фотография слишком большая или не поддерживается", "error"); }
            finally { setProcessing(false); }
          }} />
        </label>
        <Input name="caption" placeholder="Например: монтаж кухни и освещения" required minLength={3} maxLength={180} />
        <Button size="small" loading={processing} disabled={!imageData}>Отправить на проверку</Button>
      </form>
      {items.length > 0 && <div className="rt-own-portfolio-grid">{items.map((item) => <figure key={item.id}><img src={item.image_data} alt={item.caption} /><figcaption><span>{item.caption}</span><em className={`rt-status ${item.status}`}>{item.status === "published" ? "Опубликовано" : item.status === "rejected" ? "Отклонено" : "На проверке"}</em><button aria-label="Удалить работу" onClick={() => void remove(item.id)}><Trash2 /></button></figcaption></figure>)}</div>}
    </section>
  );
}

function Profile({
  me,
  refreshMe,
  notify,
}: {
  me: Me;
  refreshMe: () => Promise<void>;
  notify?: (text: string, kind?: Toast["kind"]) => void;
}) {
  return (
    <>
      <section className="rt-profile">
        <AvatarView name={me.first_name} src={me.photo_url} size="large" />
        <h1>
          {me.first_name} {me.last_name}
        </h1>
        <p>
          {me.role === "provider" && me.provider_status === "verified"
            ? `★ ${me.rating}/10 · ${me.jobs_count} заказов`
            : `@${me.username || "профиль MAX"}`}
        </p>
      </section>
      {me.role === "provider" && me.provider_status === "verified" && notify && <ProviderPortfolioManager notify={notify} />}
      <RoleSwitch me={me} refresh={refreshMe} />
    </>
  );
}

function MiniApp() {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [blocked, setBlocked] = useState(false);
  const id = useRef(0);
  const notify = useCallback(
    (text: string, kind: Toast["kind"] = "success") => {
      const current = ++id.current;
      setToasts((x) => [...x, { id: current, text, kind }]);
      window.setTimeout(
        () => setToasts((x) => x.filter((y) => y.id !== current)),
        3500,
      );
    },
    [],
  );
  const refresh = useCallback(async () => {
    try {
      setMe(await api<Me>("/me"));
    } catch (error) {
      if (error instanceof Error && error.message === "403") setBlocked(true);
      else notify("Не удалось подтвердить профиль MAX", "error");
    } finally {
      setLoading(false);
    }
  }, [notify]);
  useEffect(() => {
    const timer = window.setTimeout(() => void refresh(), 0);
    return () => window.clearTimeout(timer);
  }, [refresh]);
  if (loading)
    return (
      <div className="rt-boot">
        <Logo />
        <Spinner />
      </div>
    );
  if (blocked)
    return (
      <div className="rt-verification-state">
        <span>
          <ShieldCheck />
        </span>
        <h1>Доступ ограничен</h1>
        <p>
          Аккаунт временно заблокирован. Обратитесь в поддержку RS Tech через
          чат с ботом.
        </p>
      </div>
    );
  if (!me) return <Landing />;
  return (
    <div className="rt-app">
      {!me.role ? (
        <RolePicker me={me} refresh={refresh} notify={notify} />
      ) : me.role === "customer" ? (
        <CustomerApp me={me} refreshMe={refresh} notify={notify} />
      ) : (
        <ProviderApp me={me} refreshMe={refresh} notify={notify} />
      )}
      <Toasts items={toasts} />
    </div>
  );
}

const defaultDocuments: AdminDocument[] = Object.entries(legalPages).map(
  ([path, page]) => ({ ...page, slug: path.slice(1) }),
);
const documentText = (page: AdminDocument) =>
  page.sections
    .map((section) => `## ${section.title}\n${section.text.join("\n\n")}`)
    .join("\n\n");
const parseDocumentText = (value: string) =>
  value
    .split(/\n(?=## )/)
    .map((block) => {
      const [heading, ...body] = block.trim().split("\n");
      return {
        title: heading.replace(/^##\s*/, "").trim(),
        text: body
          .join("\n")
          .split(/\n\s*\n/)
          .map((x) => x.trim())
          .filter(Boolean),
      };
    })
    .filter((section) => section.title && section.text.length);

function AdminDocumentForm({
  page,
  token,
  reload,
}: {
  page: AdminDocument;
  token: string;
  reload: () => Promise<void>;
}) {
  const [loading, setLoading] = useState(false);
  const save = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const form = new FormData(event.currentTarget);
    try {
      await api(
        `/admin/documents/${page.slug}`,
        {
          method: "POST",
          body: JSON.stringify({
            title: form.get("title"),
            updated: form.get("updated"),
            sections: parseDocumentText(String(form.get("content") || "")),
          }),
        },
        token,
      );
      await reload();
    } finally {
      setLoading(false);
    }
  };
  return (
    <form className="admin-document-form" onSubmit={save}>
      <div className="admin-doc-fields">
        <div>
          <label htmlFor={`doc-title-${page.slug}`}>Название</label>
          <Input
            id={`doc-title-${page.slug}`}
            name="title"
            defaultValue={page.title}
            required
          />
        </div>
        <div>
          <label htmlFor={`doc-date-${page.slug}`}>Редакция</label>
          <Input
            id={`doc-date-${page.slug}`}
            name="updated"
            defaultValue={page.updated}
            required
          />
        </div>
      </div>
      <label htmlFor={`doc-content-${page.slug}`}>Текст документа</label>
      <Textarea
        id={`doc-content-${page.slug}`}
        name="content"
        defaultValue={documentText(page)}
        required
      />
      <small>
        Заголовки разделов начинаются с ##. Абзацы разделяются пустой строкой.
      </small>
      <Button size="small" loading={loading}>
        Сохранить документ
      </Button>
    </form>
  );
}

function AdminDocuments({
  saved,
  token,
  reload,
}: {
  saved: AdminDocument[];
  token: string;
  reload: () => Promise<void>;
}) {
  const merged = defaultDocuments.map(
    (page) => saved.find((item) => item.slug === page.slug) || page,
  );
  const [slug, setSlug] = useState(merged[0].slug);
  const page = merged.find((item) => item.slug === slug) || merged[0];
  return (
    <section className="admin-section">
      <div>
        <h2>Документы и политики</h2>
        <p>Изменения сразу появляются на публичных страницах сервиса.</p>
      </div>
      <div className="admin-docs">
        <nav>
          {merged.map((item) => (
            <button
              className={item.slug === slug ? "active" : ""}
              key={item.slug}
              onClick={() => setSlug(item.slug)}
            >
              {item.title}
            </button>
          ))}
        </nav>
        <AdminDocumentForm
          key={`${page.slug}-${page.updated}`}
          page={page}
          token={token}
          reload={reload}
        />
      </div>
    </section>
  );
}

function AdminApp() {
  const [token, setToken] = useState(
    () => sessionStorage.getItem("rs-admin") || "",
  );
  const [data, setData] = useState<AdminData | null>(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [section, setSection] = useState("dashboard");
  const load = useCallback(
    async (value = token) => {
      if (!value) return;
      setLoading(true);
      try {
        setData(await api<AdminData>("/admin/overview", {}, value));
        setError("");
      } catch {
        sessionStorage.removeItem("rs-admin");
        setToken("");
        setData(null);
        setError("Сессия завершена. Войдите снова.");
      } finally {
        setLoading(false);
      }
    },
    [token],
  );
  useEffect(() => {
    const timer = window.setTimeout(() => void load(), 0);
    return () => window.clearTimeout(timer);
  }, [load]);
  const login = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setLoading(true);
    const form = new FormData(event.currentTarget);
    const username = form.get("username");
    const password = form.get("password");
    try {
      const result = await api<{ token: string; user: AdminStaff }>("/admin/login", {
        method: "POST",
        body: JSON.stringify({ username, password }),
      });
      sessionStorage.setItem("rs-admin", result.token);
      setToken(result.token);
      await load(result.token);
    } catch {
      setError("Неверный пароль");
    } finally {
      setLoading(false);
    }
  };
  const update = async (provider: Provider, status: Status) => {
    await api(
      `/admin/providers/${encodeURIComponent(provider.user_id)}/status`,
      { method: "POST", body: JSON.stringify({ status }) },
      token,
    );
    await load();
  };
  const toggleBlocked = async (user: AdminUser) => {
    await api(
      `/admin/users/${encodeURIComponent(user.user_id)}/blocked`,
      { method: "POST", body: JSON.stringify({ blocked: !user.blocked }) },
      token,
    );
    await load();
  };
  const moderateReview = async (
    review: AdminReview,
    status: "published" | "rejected",
  ) => {
    await api(
      `/admin/reviews/${review.id}/status`,
      { method: "POST", body: JSON.stringify({ status }) },
      token,
    );
    await load();
  };
  const moderateSample = async (sample: AdminWorkSample, status: "published" | "rejected") => {
    await api(`/admin/work-samples/${sample.id}/status`, { method: "POST", body: JSON.stringify({ status }) }, token);
    await load();
  };
  const createStaff = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    try {
      await api("/admin/staff", { method: "POST", body: JSON.stringify(Object.fromEntries(form)) }, token);
      event.currentTarget.reset();
      await load();
    } catch {
      setError("Не удалось добавить сотрудника. Проверьте логин и пароль.");
    }
  };
  const updateStaff = async (staff: AdminStaff, changes: Record<string, unknown>) => {
    await api(`/admin/staff/${staff.id}`, { method: "POST", body: JSON.stringify(changes) }, token);
    await load();
  };
  if (!token || !data)
    return (
      <main className="admin-login">
        <div>
          <Logo />
          <span className="admin-mark">
            <ShieldCheck />
          </span>
          <h1>RS Tech Admin</h1>
          <p>Закрытая панель управления сервисом.</p>
          <form onSubmit={login}>
            <label htmlFor="admin-username">Логин</label>
            <Input id="admin-username" name="username" defaultValue="admin" autoComplete="username" required />
            <label htmlFor="admin-password">Пароль администратора</label>
            <Input
              id="admin-password"
              name="password"
              type="password"
              autoComplete="current-password"
              required
            />
            <Button size="large" stretched loading={loading}>
              Войти
            </Button>
            {error && <small>{error}</small>}
          </form>
        </div>
      </main>
    );
  return (
    <div className="admin-app">
      <aside>
        <Logo />
        <nav>
          <button className={section === "dashboard" ? "active" : ""} onClick={() => setSection("dashboard")}><LayoutDashboard /> Обзор</button>
          <button className={section === "providers" ? "active" : ""} onClick={() => setSection("providers")}><ShieldCheck /> Исполнители <span>{data.stats.pending}</span></button>
          <button className={section === "portfolio" ? "active" : ""} onClick={() => setSection("portfolio")}><Images /> Портфолио <span>{data.stats.portfolio}</span></button>
          <button className={section === "reviews" ? "active" : ""} onClick={() => setSection("reviews")}><Star /> Отзывы <span>{data.stats.reviews}</span></button>
          <button className={section === "users" ? "active" : ""} onClick={() => setSection("users")}><UsersRound /> Пользователи</button>
          <button className={section === "orders" ? "active" : ""} onClick={() => setSection("orders")}><BriefcaseBusiness /> Заказы</button>
          {data.current_admin.role !== "moderator" && <button className={section === "documents" ? "active" : ""} onClick={() => setSection("documents")}><ShieldCheck /> Документы</button>}
          {data.current_admin.role !== "moderator" && <button className={section === "staff" ? "active" : ""} onClick={() => setSection("staff")}><UserCog /> Сотрудники</button>}
        </nav>
        <Button
          variant="ghost"
          iconBefore={<LogOut />}
          onClick={() => {
            sessionStorage.removeItem("rs-admin");
            setToken("");
            setData(null);
          }}
        >
          Выйти
        </Button>
      </aside>
      <main>
        <header>
          <div>
            <span>{data.current_admin.display_name} · {data.current_admin.role === "owner" ? "Владелец" : data.current_admin.role === "admin" ? "Администратор" : "Модератор"}</span>
            <h1>{section === "dashboard" ? "Обзор сервиса" : section === "providers" ? "Исполнители" : section === "portfolio" ? "Портфолио" : section === "reviews" ? "Отзывы" : section === "users" ? "Пользователи" : section === "orders" ? "Заказы" : section === "documents" ? "Документы" : "Сотрудники"}</h1>
          </div>
          <Button
            variant="secondary"
            loading={loading}
            onClick={() => void load()}
          >
            Обновить
          </Button>
        </header>
        {section === "dashboard" && <section className="admin-stats">
          <article>
            <span>Пользователи</span>
            <b>{data.stats.users}</b>
          </article>
          <article className="attention">
            <span>Ждут проверки</span>
            <b>{data.stats.pending}</b>
          </article>
          <article>
            <span>Исполнители</span>
            <b>{data.stats.providers}</b>
          </article>
          <article>
            <span>Все заказы</span>
            <b>{data.stats.tasks}</b>
          </article>
          <article>
            <span>Отзывы на проверке</span>
            <b>{data.stats.reviews}</b>
          </article>
          <article>
            <span>Заблокированы</span>
            <b>{data.stats.blocked}</b>
          </article>
          <article className="attention">
            <span>Работы на проверке</span>
            <b>{data.stats.portfolio}</b>
          </article>
        </section>}
        {section === "users" && <section className="admin-section">
          <div>
            <h2>Пользователи</h2>
            <p>Все заказчики и исполнители, которые открывали приложение.</p>
          </div>
          <div className="admin-users">
            {data.users.map((user) => (
              <article key={user.user_id}>
                <div>
                  <b>
                    {user.first_name} {user.last_name}
                  </b>
                  <span>@{user.username || "без username"}</span>
                </div>
                <em>
                  {user.role === "provider"
                    ? "Исполнитель"
                    : user.role === "customer"
                      ? "Заказчик"
                      : "Без роли"}
                </em>
                <small>
                  {user.provider_status === "verified"
                    ? "Верифицирован"
                    : user.provider_status}
                </small>
                <Button
                  size="small"
                  variant={user.blocked ? "secondary" : "destructive"}
                  onClick={() => void toggleBlocked(user)}
                >
                  {user.blocked ? "Разблокировать" : "Заблокировать"}
                </Button>
              </article>
            ))}
          </div>
        </section>}
        {section === "reviews" && <section className="admin-section">
          <div>
            <h2>Модерация отзывов</h2>
            <p>Отзывы появятся в профилях только после публикации.</p>
          </div>
          <div className="admin-reviews">
            {data.reviews.length ? (
              data.reviews.map((review) => (
                <article key={review.id}>
                  <div>
                    <span>
                      {review.customer_first_name} {review.customer_last_name} →{" "}
                      {review.provider_first_name} {review.provider_last_name}
                    </span>
                    <b>{review.task_title}</b>
                  </div>
                  <div className="admin-review-rating"><RatingStars value={review.rating} /><b>{review.rating}/10</b></div>
                  <p>{review.text}</p>
                  <em className={`admin-status ${review.status}`}>
                    {review.status === "pending"
                      ? "На модерации"
                      : review.status === "published"
                        ? "Опубликован"
                        : "Отклонён"}
                  </em>
                  <div className="admin-actions">
                    <Button
                      size="small"
                      disabled={review.status === "published"}
                      onClick={() => void moderateReview(review, "published")}
                    >
                      Опубликовать
                    </Button>
                    <Button
                      size="small"
                      variant="destructive"
                      disabled={review.status === "rejected"}
                      onClick={() => void moderateReview(review, "rejected")}
                    >
                      Отклонить
                    </Button>
                  </div>
                </article>
              ))
            ) : (
              <div className="rt-empty">
                <Star />
                <b>Отзывов пока нет</b>
              </div>
            )}
          </div>
        </section>}
        {section === "portfolio" && <section className="admin-section">
          <div><h2>Модерация портфолио</h2><p>Публикуйте только реальные и качественные примеры работ.</p></div>
          <div className="admin-portfolio">
            {data.portfolio.length ? data.portfolio.map((item) => <article key={item.id}>
              <img src={item.image_data} alt={item.caption} />
              <div><b>{item.first_name} {item.last_name}</b><p>{item.caption}</p><em className={`admin-status ${item.status}`}>{item.status === "published" ? "Опубликовано" : item.status === "rejected" ? "Отклонено" : "На проверке"}</em><div className="admin-actions"><Button size="small" disabled={item.status === "published"} onClick={() => void moderateSample(item, "published")}>Опубликовать</Button><Button size="small" variant="destructive" disabled={item.status === "rejected"} onClick={() => void moderateSample(item, "rejected")}>Отклонить</Button></div></div>
            </article>) : <div className="rt-empty"><Images /><b>Работ на проверке нет</b></div>}
          </div>
        </section>}
        {section === "documents" && data.current_admin.role !== "moderator" && <AdminDocuments saved={data.documents} token={token} reload={load} />}
        {section === "providers" && <section className="admin-section">
          <div>
            <h2>Исполнители</h2>
            <p>Проверяйте описание, категории и профиль MAX.</p>
          </div>
          <div className="admin-table">
            {data.providers.length ? (
              data.providers.map((item) => (
                <article key={item.user_id}>
                  <div className="admin-person">
                    <AvatarView name={item.name} src={item.photo_url} />
                    <div>
                      <b>{item.name}</b>
                      <span>
                        @{item.username || "без username"} · {item.city}
                      </span>
                    </div>
                  </div>
                  <div className="rt-tags">
                    {item.categories.map((x) => (
                      <span key={x}>{x}</span>
                    ))}
                  </div>
                  <p>{item.bio}</p>
                  <em className={`admin-status ${item.status}`}>
                    {item.status === "pending"
                      ? "На проверке"
                      : item.status === "verified"
                        ? "Подтверждён"
                        : "Отклонён"}
                  </em>
                  <div className="admin-actions">
                    <Button
                      size="small"
                      onClick={() => void update(item, "verified")}
                      disabled={item.status === "verified"}
                    >
                      Подтвердить
                    </Button>
                    <Button
                      size="small"
                      variant="destructive"
                      onClick={() => void update(item, "rejected")}
                      disabled={item.status === "rejected"}
                    >
                      Отклонить
                    </Button>
                  </div>
                </article>
              ))
            ) : (
              <div className="rt-empty">
                <UsersRound />
                <b>Анкет пока нет</b>
              </div>
            )}
          </div>
        </section>}
        {section === "orders" && <section className="admin-section">
          <div>
            <h2>Последние заказы</h2>
            <p>Контроль размещённых заявок.</p>
          </div>
          <div className="admin-orders">
            {data.tasks.map((task) => (
              <div key={task.id}>
                <span>#{task.id}</span>
                <b>{task.title}</b>
                <small>
                  {task.first_name} {task.last_name} · {task.status}
                </small>
              </div>
            ))}
          </div>
        </section>}
        {section === "staff" && data.current_admin.role !== "moderator" && <section className="admin-section">
          <div><h2>Сотрудники админки</h2><p>Создавайте отдельные учётные записи и отключайте доступ без смены общего пароля.</p></div>
          <form className="admin-staff-form" onSubmit={createStaff}>
            <div><label htmlFor="staff-name">Имя сотрудника</label><Input id="staff-name" name="display_name" required minLength={2} /></div>
            <div><label htmlFor="staff-login">Логин</label><Input id="staff-login" name="username" required minLength={3} autoComplete="off" /></div>
            <div><label htmlFor="staff-password">Пароль</label><Input id="staff-password" name="password" type="password" required minLength={8} autoComplete="new-password" /></div>
            <div><label htmlFor="staff-role">Роль</label><select id="staff-role" name="role" defaultValue="moderator"><option value="moderator">Модератор</option><option value="admin">Администратор</option></select></div>
            <Button size="small">Добавить сотрудника</Button>
          </form>
          {error && <p className="admin-inline-error">{error}</p>}
          <div className="admin-staff-list">
            <article className="owner"><div><b>Владелец</b><span>admin</span></div><em>Полный доступ</em><strong>Активен</strong></article>
            {data.staff.map((staff) => <article key={staff.id}><div><b>{staff.display_name}</b><span>@{staff.username}</span></div><select value={staff.role} onChange={(event) => void updateStaff(staff, { role: event.target.value })}><option value="moderator">Модератор</option><option value="admin">Администратор</option></select><small>{staff.last_login ? `Вход: ${staff.last_login}` : "Ещё не входил"}</small><Button size="small" variant={staff.active ? "destructive" : "secondary"} onClick={() => void updateStaff(staff, { active: !staff.active })}>{staff.active ? "Отключить" : "Включить"}</Button></article>)}
          </div>
        </section>}
      </main>
    </div>
  );
}

function Legal({ page, slug }: { page: LegalPage; slug: string }) {
  const [current, setCurrent] = useState(page);
  useEffect(() => {
    let active = true;
    void api<LegalPage>(`/documents/${slug}`)
      .then((value) => {
        if (active) setCurrent(value);
      })
      .catch(() => undefined);
    return () => {
      active = false;
    };
  }, [slug]);
  return (
    <div className="legal-page">
      <header>
        <div className="app-shell">
          <Logo />
          <a href="/">В приложение</a>
        </div>
      </header>
      <main className="legal-content">
        <a className="back" href="/">
          <ArrowLeft />
          Назад
        </a>
        <h1>{current.title}</h1>
        <p className="updated">Редакция: {current.updated}</p>
        {current.sections.map((section) => (
          <section key={section.title}>
            <h2>{section.title}</h2>
            {section.text.map((text) => (
              <p key={text}>{text}</p>
            ))}
          </section>
        ))}
      </main>
    </div>
  );
}
function App() {
  useKeyboardLayout();
  const page = useMemo(() => legalPages[window.location.pathname], []);
  if (window.location.pathname === "/admin-pro") return <AdminApp />;
  if (page)
    return <Legal page={page} slug={window.location.pathname.slice(1)} />;
  return window.WebApp?.initData || window.Telegram?.WebApp?.initData ? (
    <MiniApp />
  ) : (
    <Landing />
  );
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <MaxUI resetBody>
      <App />
    </MaxUI>
  </StrictMode>,
);
